import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api, { TOKEN_STORAGE_KEY } from '../api/client';
import { createDispatchRefresh } from '../api/dispatchRefresh';
import { subscribeToActiveUnitStream } from '../api/statusStream';
import { shouldRefreshWaze } from '../api/wazeRefresh';

const EMPTY = { units: [], incidents: [], emergencies: [], incompleteCount: 0, incidentVersion: null };
const REFRESH_ERROR = 'Unable to refresh dispatch. Showing the last received information.';
const validVersion = value => typeof value === 'string' && /^[a-f0-9]{64}$/i.test(value);

function readMobileSnapshot(response, previous) {
  const data = response?.data;
  const version = validVersion(data?.incidentVersion) ? data.incidentVersion.toUpperCase() : null;
  const reuse = data?.incidentsUnchanged === true;
  // Omitted calls require the owner's last applied receipt. Older complete
  // responses stay usable, but cannot enable conditional reads.
  if ((data?.incidentVersion != null && !version) || (data?.incidentsUnchanged !== undefined &&
      typeof data.incidentsUnchanged !== 'boolean') || (reuse && (data.incidents !== null || !version ||
      !validVersion(previous?.incidentVersion) || version !== previous.incidentVersion.toUpperCase()))) throw new Error(REFRESH_ERROR);
  const incidents = reuse ? previous.incidents : data?.incidents;
  if (response?.status !== 200 || typeof data?.serverTimeUtc !== 'string' || !Number.isFinite(Date.parse(data.serverTimeUtc)) ||
      !Array.isArray(data.units) || data.units.some(unit => !unit || !Number.isSafeInteger(unit.userId) || unit.userId <= 0 ||
        typeof unit.rangerId !== 'string' || !unit.rangerId.trim()) ||
      new Set(data.units.map(unit => unit.userId)).size !== data.units.length ||
      !Array.isArray(incidents) || incidents.some(call => !call || !Number.isSafeInteger(call.id) || call.id <= 0 || call.isArchived) ||
      new Set(incidents.map(call => call.id)).size !== incidents.length ||
      !Array.isArray(data.emergencies) || data.emergencies.some(alert => !alert || !Number.isSafeInteger(alert.userId) || alert.userId <= 0 ||
        typeof alert.cycleId !== 'string' || !/^[a-f0-9]{64}$/i.test(alert.cycleId) ||
        typeof alert.activatedAtUtc !== 'string' || !Number.isFinite(Date.parse(alert.activatedAtUtc))) ||
      new Set(data.emergencies.map(alert => alert.cycleId.toUpperCase())).size !== data.emergencies.length) {
    throw new Error(REFRESH_ERROR);
  }
  return { units: data.units, incidents, emergencies: data.emergencies, incidentVersion: version };
}

export function readMobileDispatch(snapshot, incomplete, previous) {
  if (incomplete?.status !== 200 || !Array.isArray(incomplete.data?.items) || !Number.isSafeInteger(incomplete.data.total) ||
      incomplete.data.total < incomplete.data.items.length || incomplete.data.total < 0) throw new Error(REFRESH_ERROR);
  return { ...readMobileSnapshot(snapshot, previous), incompleteCount: incomplete.data.total };
}

// Stream payloads are invalidation hints, never authoritative snapshots. One
// account-scoped reader handles initial load, mutations, reconnect and polling.
export default function useMobileDispatch(user, refreshWaze) {
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  const rangerId = user?.rangerId;
  const owner = user?.id && rangerId && token ? JSON.stringify([user.id, rangerId, token]) : null;
  const ownerRef = useRef(owner);
  ownerRef.current = owner;
  const wazeRef = useRef(refreshWaze);
  wazeRef.current = refreshWaze;
  const [state, setState] = useState({ owner: null, snapshot: EMPTY, loading: false, error: '', live: false });
  const controller = useMemo(() => {
    let disposed = false, countRefreshPending = true, lastSnapshot = null;
    const ownedState = previous => previous.owner === owner ? previous
      : { owner, snapshot: EMPTY, loading: false, error: '', live: false };
    const currentSession = () => !disposed && owner && ownerRef.current === owner &&
      localStorage.getItem(TOKEN_STORAGE_KEY) === token ? owner : null;
    const refresh = createDispatchRefresh({ currentSession,
      load: async () => {
        if (!currentSession()) return null;
        const options = { timeout: 15000, expectedSessionToken: token };
        const snapshot = api.get('/status/snapshot', { ...options, ...(validVersion(lastSnapshot?.incidentVersion)
          ? { params: { knownIncidentVersion: lastSnapshot.incidentVersion } } : {}) });
        if (!countRefreshPending && lastSnapshot) {
          return { ...readMobileSnapshot(await snapshot, lastSnapshot), incompleteCount: lastSnapshot.incompleteCount };
        }
        const [dispatch, count] = await Promise.all([
          snapshot,
          api.get('/incidents', { ...options, params: { status: 'incomplete', rangerId, pageSize: 1 } }),
        ]);
        return readMobileDispatch(dispatch, count, lastSnapshot);
      },
      apply: snapshot => {
        lastSnapshot = snapshot;
        countRefreshPending = false;
        setState(previous => ({ ...ownedState(previous), snapshot, error: '' }));
      },
    });
    return {
      activate: () => { disposed = false; },
      dispose: () => { disposed = true; },
      currentSession,
      setLive(live) {
        if (currentSession()) setState(previous => ({ ...ownedState(previous), live }));
      },
      async fetch({ refreshCount = true } = {}) {
        if (!currentSession()) return false;
        if (refreshCount) countRefreshPending = true;
        setState(previous => ({ ...ownedState(previous), loading: true }));
        try { await refresh(); return Boolean(currentSession()); }
        catch {
          if (currentSession()) setState(previous => ({ ...previous, owner, error: REFRESH_ERROR, live: false }));
          return false;
        } finally {
          if (currentSession()) setState(previous => ({ ...previous, owner, loading: false }));
        }
      },
      acknowledge(incidentId, iso) {
        if (!currentSession()) return;
        setState(previous => previous.owner === owner ? { ...previous, snapshot: { ...previous.snapshot,
          incidents: previous.snapshot.incidents.map(call => call.id === incidentId
            ? { ...call, statusCheckAcknowledgedUtc: iso } : call) } } : previous);
        // Invalidate a read already in flight before the acknowledgment committed.
        this.fetch();
      },
    };
  }, [owner, token, rangerId]);

  useEffect(() => {
    controller.activate();
    if (!owner) return () => controller.dispose();
    let cancelled = false, stream = null, retry = null, generation = 0;
    const closeStream = () => { generation++; stream?.close(); stream = null; };
    const clearRetry = () => { if (retry !== null) clearTimeout(retry); retry = null; };
    function connect() {
      if (cancelled || !controller.currentSession()) return;
      clearRetry(); closeStream();
      controller.setLive(false);
      const attempt = generation;
      const refreshLive = options => controller.fetch(options).then(ok => {
        if (!cancelled && ok && attempt === generation) controller.setLive(true);
      });
      try {
        stream = subscribeToActiveUnitStream({
          onOpen: () => {
            if (!cancelled && attempt === generation) refreshLive();
          },
          onMessage: payload => {
            if (cancelled || attempt !== generation || !controller.currentSession()) return;
            if (shouldRefreshWaze(payload?.reason)) wazeRef.current?.();
            refreshLive({ refreshCount: payload?.reason !== 'ping-update' });
          },
          onError: () => {
            if (cancelled || attempt !== generation) return;
            controller.setLive(false); closeStream();
            if (retry === null) retry = setTimeout(connect, 5000);
          },
        });
      } catch {
        controller.setLive(false);
        if (retry === null) retry = setTimeout(connect, 5000);
      }
    }
    const resume = () => {
      if (cancelled || document.visibilityState === 'hidden' || !controller.currentSession()) return;
      controller.fetch(); wazeRef.current?.(); connect();
    };
    controller.fetch(); connect();
    const poll = setInterval(() => controller.fetch(), 30000);
    window.addEventListener('online', resume);
    window.addEventListener('focus', resume);
    document.addEventListener('visibilitychange', resume);
    return () => {
      cancelled = true; controller.dispose(); clearRetry(); closeStream(); clearInterval(poll);
      window.removeEventListener('online', resume);
      window.removeEventListener('focus', resume);
      document.removeEventListener('visibilitychange', resume);
    };
  }, [controller, owner]);

  const refresh = useCallback(() => controller.fetch(), [controller]);
  const acknowledge = useCallback((id, iso) => controller.acknowledge(id, iso), [controller]);
  const visible = state.owner === owner && owner ? state : null;
  return { ...(visible?.snapshot || EMPTY), loading: visible?.loading || false,
    error: visible?.error || '', live: visible?.live || false, refresh, acknowledge };
}
